/**
 * Session recovery for the Troubleshooter's requests.
 *
 * Every other page routes failures through `universalAPIErrorHandler`, which
 * refreshes the bearer token (`token_secret` cookie → `/auth/refresh/`) and
 * retries, or sends the user back to Cognito when it cannot. The
 * Troubleshooter handles its own errors (a missing endpoint is a "gap", a
 * failed one an "error" card), so an expired session showed up as every card
 * failing at once — "Network Error" ×4 and "Authentication credentials were
 * not provided" — with no way back but a manual reload.
 *
 * This wraps a request: on an auth failure it refreshes the session once
 * (deduplicated across the ~10 requests that fire together) and retries the
 * call with the new token; if the refresh fails `getRefreshToken` already
 * redirects to Cognito, and the original error is rethrown.
 */
import { AxiosError } from "axios";
import { getCookie } from "../../../utils/getCookie";
import { getRefreshToken } from "../../../utils/getRefreshToken";
import { handleRedirect } from "../../Login";

export const SESSION_EXPIRED_MESSAGE =
  "Your session expired. Signing you back in… if this message stays, reload the page.";

const TOKEN_BODY_MARKERS = [
  "Invalid token",
  "Token is expired",
  "Token not found",
  "SocialToken matching query does not exist",
  "Returned invalid token from cache",
  "Authentication credentials were not provided",
];

const bodyText = (error: AxiosError | undefined): string => {
  const data = error?.response?.data;
  if (data == null) return "";
  return typeof data === "string" ? data : JSON.stringify(data);
};

/**
 * Whether a failed request means "the session is gone", as opposed to a
 * missing endpoint, a server fault or a real network problem.
 *
 * 401 always. 403 when the body carries one of the token markers the app's
 * handler looks for. And — the shape the page actually showed — no response
 * at all ("Network Error") while the bearer cookie is gone but a refresh
 * cookie remains: the browser never got a readable answer, and the only
 * thing that changed is the token.
 */
export const isAuthFailure = (error: unknown): boolean => {
  const err = error as AxiosError | undefined;
  const status = err?.response?.status;
  const token = getCookie("token");
  const refresh = getCookie("token_secret");
  if (status === 401) return true;
  if (status === 403) {
    const body = bodyText(err);
    if (TOKEN_BODY_MARKERS.some((marker) => body.includes(marker))) return true;
    return Boolean(!token && refresh);
  }
  if (status == null && err && "message" in err)
    return Boolean(!token && refresh);
  return false;
};

let refreshing: Promise<boolean> | null = null;

/** Refresh the session once for however many callers ask at the same time. True when a new token is in place. */
export const refreshSessionOnce = (): Promise<boolean> => {
  if (!refreshing) {
    refreshing = new Promise<boolean>((resolve) => {
      if (!getCookie("token_secret")) {
        resolve(false);
        return;
      }
      let renewed = false;
      // The app's other refreshers (universalAPIErrorHandler,
      // concurrentAPIErrorHandler) dedupe on this flag; set it so they wait
      // for this refresh instead of racing it with a second /auth/refresh/.
      try {
        localStorage.setItem("tokenRefreshing", "true");
      } catch {
        // storage unavailable: nothing to dedupe on
      }
      Promise.resolve(
        getRefreshToken(undefined, () => {
          renewed = true;
          resolve(true);
        })
      )
        .then(() => {
          if (!renewed) resolve(false);
        })
        .catch(() => resolve(false));
    }).finally(() => {
      refreshing = null;
      try {
        localStorage.setItem("tokenRefreshing", "false");
      } catch {
        // ignore
      }
    });
  }
  return refreshing;
};

/** Run `call`; on an auth failure refresh the session once and run it again. */
export const withSessionRecovery = async <T>(
  call: () => Promise<T>
): Promise<T> => {
  try {
    return await call();
  } catch (error) {
    if (!isAuthFailure(error)) throw error;
    const refreshed = await refreshSessionOnce();
    if (!refreshed) throw error;
    return call();
  }
};

/** Test hook. */
export const resetSessionRecovery = () => {
  refreshing = null;
};

/**
 * Before the page's first requests: make sure a bearer token exists.
 *
 * RequireAuth does this when a route mounts, but the Troubleshooter keeps
 * refreshing itself every minute for hours, and the token cookie lives one
 * hour (`authStateManager`, maxAge 3600) while the refresh cookie persists —
 * so a page left open outlives its token. With a refresh cookie the session
 * is renewed here, once, before ten requests fail; with neither cookie the
 * user is sent to Cognito the way RequireAuth would, with the current path
 * remembered. Returns whether requests can proceed.
 */
export const ensureSession = async (): Promise<boolean> => {
  if (getCookie("token")) return true;
  if (getCookie("token_secret")) return refreshSessionOnce();
  localStorage.setItem(
    "redirectURL",
    window.location.pathname + window.location.search + window.location.hash
  );
  handleRedirect();
  return false;
};
